import React, { useEffect, useState } from "react";
import {
  Modal,
  Descriptions,
  Table,
  Tag,
  Divider,
  Space,
  Button,
  Typography,
  Row,
  Col,
  Collapse,
  Spin,
} from "antd";
import { PrinterOutlined, DownloadOutlined } from "@ant-design/icons";
import { Bill, SavedBillItem } from "../../types/Billing";
import billingService from "../../services/billingService";
import dayjs from "dayjs";

const { Title, Text } = Typography;

interface BillDetailsModalProps {
  bill: Bill;
  visible: boolean;
  onClose: () => void;
}

const BillDetailsModal: React.FC<BillDetailsModalProps> = ({
  bill: billProp,
  visible,
  onClose,
}) => {
  const [bill, setBill] = useState<Bill>(billProp);
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState<
    Array<{
      _id: string;
      version: number;
      summary?: string;
      createdAt: string;
      snapshot: any;
      saleReturn?: {
        returnNumber?: string;
        netRefundAmount?: number;
        refundMethod?: string;
      };
    }>
  >([]);

  useEffect(() => {
    if (!visible || !billProp?._id) return;
    setBill(billProp);
    let cancelled = false;
    setLoading(true);
    Promise.all([
      billingService.getBillById(billProp._id),
      billingService.getBillVersions(billProp._id).catch(() => ({
        current: { version: 1 },
        versions: [],
      })),
    ])
      .then(([fresh, versionData]) => {
        if (cancelled) return;
        setBill(fresh);
        setVersions(versionData.versions || []);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, billProp]);

  const patientData =
    typeof bill.patient === "string" ? bill.patientDetails : bill.patient;
  const customerName =
    patientData?.name || bill.customer?.name || "Walk-in Customer";
  const customerPhone = patientData?.phone || bill.customer?.phone || "—";
  const customerAge = patientData?.age ?? bill.customer?.age;
  const customerGender = patientData?.gender || bill.customer?.gender;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "paid":
        return "success";
      case "partially_paid":
      case "partial":
        return "warning";
      case "pending":
        return "default";
      case "refunded":
        return "processing";
      case "overdue":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "paid":
        return "Paid";
      case "partially_paid":
      case "partial":
        return "Partially Paid";
      case "pending":
        return "Pending";
      case "refunded":
        return "Refunded";
      case "overdue":
        return "Overdue";
      default:
        return status;
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    window.open(`/billing/${bill._id}/print`, "_blank");
  };

  const itemColumns = [
    {
      title: "Product",
      dataIndex: "name",
      key: "name",
      render: (_: unknown, row: SavedBillItem) =>
        row.name || row.stockName || "—",
    },
    {
      title: "Batch",
      dataIndex: "batchNumber",
      key: "batchNumber",
      width: 90,
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      align: "center" as const,
      width: 70,
    },
    {
      title: "Rate",
      dataIndex: "unitPrice",
      key: "unitPrice",
      align: "right" as const,
      width: 90,
      render: (price: number) => `₹${(price || 0).toFixed(2)}`,
    },
    {
      title: "Disc%",
      dataIndex: "discount",
      key: "discount",
      align: "center" as const,
      width: 70,
      render: (discount: number) => `${discount || 0}%`,
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      align: "right" as const,
      width: 100,
      render: (total: number) => `₹${(total || 0).toFixed(2)}`,
    },
  ];

  const renderSnapshot = (snap: any) => {
    if (!snap) return null;
    return (
      <div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Grand total ₹{(snap.grandTotal || 0).toFixed(2)} · Paid ₹
          {(snap.amountPaid || 0).toFixed(2)} · {snap.items?.length || 0}{" "}
          item(s)
        </Text>
        <Table
          size="small"
          pagination={false}
          style={{ marginTop: 8 }}
          rowKey={(_, i) => String(i)}
          dataSource={snap.items || []}
          columns={[
            {
              title: "Item",
              dataIndex: "name",
              key: "name",
            },
            {
              title: "Qty",
              dataIndex: "quantity",
              key: "quantity",
              width: 60,
            },
            {
              title: "Amt",
              dataIndex: "total",
              key: "total",
              width: 90,
              render: (v: number) => `₹${(v || 0).toFixed(2)}`,
            },
          ]}
        />
      </div>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <Text>Bill Details</Text>
          <Tag>v{bill.version || 1}</Tag>
          <Tag color={getStatusColor(bill.paymentStatus)}>
            {getStatusText(bill.paymentStatus)}
          </Tag>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="print" icon={<PrinterOutlined />} onClick={handlePrint}>
          Print
        </Button>,
        <Button
          key="download"
          icon={<DownloadOutlined />}
          onClick={handleDownload}
        >
          Download PDF
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        <div style={{ padding: "20px 0" }}>
          <div style={{ textAlign: "center", marginBottom: "30px" }}>
            <Title level={3} style={{ margin: 0 }}>
              MEDICAL BILL
            </Title>
            <Text type="secondary">
              Bill #{bill.billNumber}
              {(bill.version || 1) > 1 ? ` · current v${bill.version}` : ""}
            </Text>
          </div>

          <Row gutter={32} style={{ marginBottom: "30px" }}>
            <Col span={12}>
              <Title level={5}>Bill Information</Title>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Bill Number">
                  {bill.billNumber}
                </Descriptions.Item>
                <Descriptions.Item label="Version">
                  v{bill.version || 1}
                </Descriptions.Item>
                <Descriptions.Item label="Date">
                  {dayjs(bill.createdAt).format("DD MMM YYYY, hh:mm A")}
                </Descriptions.Item>
                <Descriptions.Item label="Payment Method">
                  <Tag>
                    {bill.paymentMethod
                      ? bill.paymentMethod.charAt(0).toUpperCase() +
                        bill.paymentMethod.slice(1)
                      : "N/A"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={getStatusColor(bill.paymentStatus)}>
                    {getStatusText(bill.paymentStatus)}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col span={12}>
              <Title level={5}>Customer Information</Title>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Name">
                  {customerName}
                </Descriptions.Item>
                <Descriptions.Item label="Patient ID">
                  {patientData?.patientId || "Walk-in"}
                </Descriptions.Item>
                <Descriptions.Item label="Phone">
                  {customerPhone}
                </Descriptions.Item>
                <Descriptions.Item label="Age & Gender">
                  {customerAge != null ? `${customerAge} years` : "—"}
                  {customerGender ? `, ${customerGender}` : ""}
                </Descriptions.Item>
                {(bill as any).doctorName && (
                  <Descriptions.Item label="Doctor">
                    {(bill as any).doctorName}
                  </Descriptions.Item>
                )}
                {(bill as any).prescriptionNumber && (
                  <Descriptions.Item label="Prescription">
                    {(bill as any).prescriptionNumber}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Col>
          </Row>

          <Divider />

          <Title level={5} style={{ marginBottom: "16px" }}>
            Items (current)
          </Title>
          <Table
            columns={itemColumns}
            dataSource={(bill.items || []).map(
              (item: SavedBillItem, index: number) => ({
                ...item,
                key: index,
              })
            )}
            pagination={false}
            size="small"
            bordered
            locale={{ emptyText: "All items returned" }}
            style={{ marginBottom: "24px" }}
          />

          <Row justify="end">
            <Col span={8}>
              <div
                style={{
                  background: "#fafafa",
                  padding: "16px",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                  }}
                >
                  <Text>Subtotal:</Text>
                  <Text>₹{(bill.subtotal || 0).toFixed(2)}</Text>
                </div>
                {(bill.billDiscount ?? 0) > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "8px",
                    }}
                  >
                    <Text>Bill Discount:</Text>
                    <Text>₹{Number(bill.billDiscount).toFixed(2)}</Text>
                  </div>
                )}
                <Divider style={{ margin: "12px 0" }} />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                  }}
                >
                  <Text strong>Grand Total:</Text>
                  <Text strong style={{ fontSize: "16px" }}>
                    ₹{(bill.grandTotal || 0).toFixed(2)}
                  </Text>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                  }}
                >
                  <Text>Amount Paid:</Text>
                  <Text style={{ color: "#52c41a" }}>
                    ₹{(bill.amountPaid || 0).toFixed(2)}
                  </Text>
                </div>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Text strong>Balance:</Text>
                  <Text
                    strong
                    style={{
                      color: (bill.balance || 0) > 0 ? "#ff4d4f" : "#52c41a",
                    }}
                  >
                    ₹{(bill.balance || 0).toFixed(2)}
                  </Text>
                </div>
              </div>
            </Col>
          </Row>

          {versions.length > 0 && (
            <>
              <Divider />
              <Title level={5}>Previous versions</Title>
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
                Snapshots saved before each return. Current bill is v
                {bill.version || 1}.
              </Text>
              <Collapse
                size="small"
                items={versions.map((v) => ({
                  key: v._id,
                  label: (
                    <Space wrap>
                      <Text strong>v{v.version}</Text>
                      <Text type="secondary">
                        {dayjs(v.createdAt).format("DD MMM YYYY, hh:mm A")}
                      </Text>
                      {v.saleReturn?.returnNumber && (
                        <Tag>
                          {v.saleReturn.returnNumber}
                          {v.saleReturn.netRefundAmount != null
                            ? ` · refund ₹${Number(
                                v.saleReturn.netRefundAmount
                              ).toFixed(2)}`
                            : ""}
                        </Tag>
                      )}
                    </Space>
                  ),
                  children: renderSnapshot(v.snapshot),
                }))}
              />
            </>
          )}

          {bill.notes && (
            <>
              <Divider />
              <div>
                <Title level={5}>Notes</Title>
                <Text style={{ whiteSpace: "pre-wrap" }}>{bill.notes}</Text>
              </div>
            </>
          )}

          <div
            style={{
              textAlign: "center",
              marginTop: "40px",
              padding: "20px 0",
              borderTop: "1px solid #d9d9d9",
            }}
          >
            <Text type="secondary">
              Generated on {dayjs().format("DD MMM YYYY, hh:mm A")}
            </Text>
          </div>
        </div>
      </Spin>
    </Modal>
  );
};

export default BillDetailsModal;
